import React, { useState, useEffect } from 'react';
import { Category, Subcategory, CreateSubcategoryDto } from '@/domain/entities';
import { categoryService, subcategoryService } from '@/application/services';
import { HttpError } from '@/domain/entities';

export const CategoriesPage: React.FC = () => {
  
  // Categories state
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editForm, setEditForm] = useState({ nameRu: '', description: '' });
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [categorySubcategories, setCategorySubcategories] = useState<Record<number, Subcategory[]>>({});
  const [loadingSubcategories, setLoadingSubcategories] = useState<Set<number>>(new Set());

  // Subcategories state (for modals)
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState<Subcategory | null>(null);
  const [subcategoryFormData, setSubcategoryFormData] = useState<Partial<CreateSubcategoryDto>>({
    slug: '',
    nameRu: '',
    nameEn: '',
    description: '',
  });

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    // Автоматически разворачиваем все категории с подкатегориями
    if (categories.length > 0) {
      const categoriesWithSubs = categories
        .filter((cat) => cat.hasSubcategories)
        .map((cat) => cat.id);
      
      if (categoriesWithSubs.length > 0) {
        setExpandedCategories(new Set(categoriesWithSubs));
        
        // Загружаем подкатегории для всех категорий с подкатегориями
        categoriesWithSubs.forEach((categoryId) => {
          if (!categorySubcategories[categoryId]) {
            loadCategorySubcategories(categoryId);
          }
        });
      }
    }
  }, [categories]);

  const loadCategories = async () => {
    setCategoriesLoading(true);
    setCategoriesError(null);
    try {
      const data = await categoryService.getCategories();
      setCategories(data);
    } catch (err) {
      if (err instanceof HttpError) {
        setCategoriesError(err.data?.error || err.message);
      } else {
        setCategoriesError('Не удалось загрузить категории');
      }
    } finally {
      setCategoriesLoading(false);
    }
  };


  const loadCategorySubcategories = async (categoryId: number) => {
    setLoadingSubcategories((prev) => new Set(prev).add(categoryId));
    try {
      const data = await subcategoryService.getSubcategories(categoryId);
      setCategorySubcategories((prev) => ({
        ...prev,
        [categoryId]: data,
      }));
    } catch (err) {
      console.error('Failed to load subcategories', err);
    } finally {
      setLoadingSubcategories((prev) => {
        const newSet = new Set(prev);
        newSet.delete(categoryId);
        return newSet;
      });
    }
  };

  const toggleCategoryExpansion = (categoryId: number) => {
    const isCurrentlyExpanded = expandedCategories.has(categoryId);
    
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (isCurrentlyExpanded) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
    
    // Загружаем подкатегории, если разворачиваем и еще не загружены
    if (!isCurrentlyExpanded && !categorySubcategories[categoryId]) {
      loadCategorySubcategories(categoryId);
    }
  };

  const handleRefreshSubcategories = (categoryId: number) => {
    loadCategorySubcategories(categoryId);
  };

  // Categories handlers
  const handleEditCategory = (category: Category) => {
    if (categoryService.isSystemCategory(category.id)) {
      alert('Системные категории нельзя редактировать');
      return;
    }
    setEditingCategory(category);
    setEditForm({
      nameRu: category.nameRu,
      description: category.description || '',
    });
  };

  const handleSaveCategory = async () => {
    if (!editingCategory) return;

    try {
      await categoryService.updateCategory(editingCategory.id, editForm);
      setEditingCategory(null);
      loadCategories();
    } catch (err) {
      if (err instanceof HttpError) {
        alert(err.data?.error || err.message);
      } else {
        alert('Не удалось обновить категорию');
      }
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (categoryService.isSystemCategory(id)) {
      alert('Системные категории нельзя удалять');
      return;
    }

    if (!confirm('Вы уверены, что хотите удалить эту категорию?')) return;

    try {
      await categoryService.deleteCategory(id);
      loadCategories();
    } catch (err) {
      if (err instanceof HttpError) {
        alert(err.data?.error || err.message);
      } else {
        alert('Не удалось удалить категорию');
      }
    }
  };

  // Subcategories handlers
  const handleCreateSubcategory = async () => {
    if (!selectedCategoryId) {
      alert('Выберите категорию');
      return;
    }

    try {
      await subcategoryService.createSubcategory({
        ...subcategoryFormData,
        categoryId: selectedCategoryId,
      } as CreateSubcategoryDto);
      setShowCreateForm(false);
      setSubcategoryFormData({ slug: '', nameRu: '', nameEn: '', description: '' });
      handleSubcategoryCreated();
    } catch (err) {
      if (err instanceof HttpError) {
        alert(err.data?.error || err.message);
      } else {
        alert('Не удалось создать подкатегорию');
      }
    }
  };

  const handleEditSubcategory = (subcategory: Subcategory) => {
    setEditingSubcategory(subcategory);
    setSubcategoryFormData({
      slug: subcategory.slug,
      nameRu: subcategory.nameRu,
      nameEn: subcategory.nameEn,
      description: subcategory.description || '',
    });
  };

  const handleUpdateSubcategory = async () => {
    if (!editingSubcategory) return;

    const categoryId = editingSubcategory.categoryId;
    try {
      await subcategoryService.updateSubcategory(editingSubcategory.id, subcategoryFormData);
      setEditingSubcategory(null);
      setSubcategoryFormData({ slug: '', nameRu: '', nameEn: '', description: '' });
      handleRefreshSubcategories(categoryId);
    } catch (err) {
      if (err instanceof HttpError) {
        alert(err.data?.error || err.message);
      } else {
        alert('Не удалось обновить подкатегорию');
      }
    }
  };

  const handleDeleteSubcategory = async (id: number, categoryId: number) => {
    if (!confirm('Вы уверены, что хотите удалить эту подкатегорию?')) return;

    try {
      await subcategoryService.deleteSubcategory(id);
      handleRefreshSubcategories(categoryId);
    } catch (err) {
      if (err instanceof HttpError) {
        alert(err.data?.error || err.message);
      } else {
        alert('Не удалось удалить подкатегорию');
      }
    }
  };

  const handleCreateSubcategoryInCategory = (categoryId: number) => {
    setSelectedCategoryId(categoryId);
    setShowCreateForm(true);
  };

  const handleSubcategoryCreated = () => {
    if (selectedCategoryId) {
      handleRefreshSubcategories(selectedCategoryId);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Категории и подкатегории</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Управление категориями и подкатегориями товаров
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto min-h-0 mt-4">
        {categoriesError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
            {categoriesError}
          </div>
        )}

        {categoriesLoading ? (
          <div className="text-center py-8 text-gray-600 dark:text-gray-400">Загрузка...</div>
        ) : (
          <div className="bg-white dark:bg-gray-800 overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                  <tr>
                    <th className="py-2.5 px-4 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider w-12 border-b border-gray-200 dark:border-gray-600">
                    </th>
                    <th className="py-2.5 px-4 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-200 dark:border-gray-600">
                      ID
                    </th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-200 dark:border-gray-600">
                      Slug
                    </th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-200 dark:border-gray-600">
                      Название (RU)
                    </th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-200 dark:border-gray-600">
                      Название (EN)
                    </th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-200 dark:border-gray-600">
                      Подкатегории
                    </th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-200 dark:border-gray-600">
                      Статус
                    </th>
                    <th className="py-2.5 px-3 text-right text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-200 dark:border-gray-600">
                      Действия
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800">
                    {categories.map((category) => {
                      const isSystem = categoryService.isSystemCategory(category.id);
                      const isEditing = editingCategory?.id === category.id;
                      const isExpanded = expandedCategories.has(category.id);
                      const subcategories = categorySubcategories[category.id] || [];
                      const isLoadingSubs = loadingSubcategories.has(category.id);

                      return (
                        <React.Fragment key={category.id}>
                          <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700">
                            <td className="whitespace-nowrap py-2.5 px-4 text-sm font-medium text-gray-900 dark:text-white">
                              {category.hasSubcategories && (
                                <button
                                  onClick={() => toggleCategoryExpansion(category.id)}
                                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-transform"
                                  style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                >
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              )}
                            </td>
                            <td className="whitespace-nowrap py-2.5 px-4 text-sm font-medium text-gray-900 dark:text-white">
                              {category.id}
                            </td>
                            <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-400">
                              {category.slug}
                            </td>
                            <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-400">
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.nameRu}
                                  onChange={(e) =>
                                    setEditForm({ ...editForm, nameRu: e.target.value })
                                  }
                                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                              ) : (
                                category.nameRu
                              )}
                            </td>
                            <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-400">
                              {category.nameEn}
                            </td>
                            <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-400">
                              {category.hasSubcategories ? (
                                <span className="text-indigo-600 dark:text-indigo-400">
                                  {isExpanded ? subcategories.length : 'Да'}
                                </span>
                              ) : (
                                'Нет'
                              )}
                            </td>
                            <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-400">
                              {isSystem && (
                                <span className="px-2 py-1 text-xs font-semibold text-blue-800 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 rounded">
                                  Системная
                                </span>
                              )}
                            </td>
                            <td className="whitespace-nowrap py-2.5 px-3 text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-2">
                                {isEditing ? (
                                  <>
                                    <button
                                      onClick={handleSaveCategory}
                                      className="px-3 py-1.5 text-sm font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                                    >
                                      Сохранить
                                    </button>
                                    <button
                                      onClick={() => setEditingCategory(null)}
                                      className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                    >
                                      Отмена
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    {category.hasSubcategories && isExpanded && (
                                      <button
                                        onClick={() => handleCreateSubcategoryInCategory(category.id)}
                                        className="px-3 py-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                                      >
                                        + Подкатегория
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleEditCategory(category)}
                                      disabled={isSystem}
                                      className="px-3 py-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      Редактировать
                                    </button>
                                    <button
                                      onClick={() => handleDeleteCategory(category.id)}
                                      disabled={isSystem}
                                      className="px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      Удалить
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <>
                              {isLoadingSubs ? (
                                <tr>
                                  <td colSpan={8} className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700">
                                    <div className="pl-8">Загрузка подкатегорий...</div>
                                  </td>
                                </tr>
                              ) : subcategories.length > 0 ? (
                                subcategories.map((subcategory) => (
                                  <tr key={subcategory.id} className="bg-gray-50 dark:bg-gray-700/30 hover:bg-gray-100 dark:hover:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700">
                                    <td></td>
                                    <td className="whitespace-nowrap py-2.5 pl-8 pr-4 text-sm font-medium text-gray-600 dark:text-gray-300">
                                      {subcategory.id}
                                    </td>
                                    <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-400">
                                      {subcategory.slug}
                                    </td>
                                    <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-400">
                                      {subcategory.nameRu}
                                    </td>
                                    <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-400">
                                      {subcategory.nameEn}
                                    </td>
                                    <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-400 dark:text-gray-500">
                                      <span className="text-xs">—</span>
                                    </td>
                                    <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-400 dark:text-gray-500">
                                      <span className="text-xs">—</span>
                                    </td>
                                    <td className="whitespace-nowrap py-2.5 px-3 text-right text-sm font-medium">
                                      <div className="flex items-center justify-end gap-2">
                                        <button
                                          onClick={() => handleEditSubcategory(subcategory)}
                                          className="px-3 py-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                                        >
                                          Редактировать
                                        </button>
                                        <button
                                          onClick={() => handleDeleteSubcategory(subcategory.id, category.id)}
                                          className="px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                                        >
                                          Удалить
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={8} className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700">
                                    <div className="pl-8">Нет подкатегорий</div>
                                  </td>
                                </tr>
                              )}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Subcategory Form Modal */}
      {(showCreateForm || editingSubcategory) && (
        <div className="fixed inset-0 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4 pointer-events-none fade-in bg-black/50">
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto border border-gray-300 dark:border-gray-700 pointer-events-auto zoom-in-95 p-6">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 -mx-6 -mt-6 mb-6 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {editingSubcategory ? 'Редактировать подкатегорию' : 'Создать подкатегорию'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingSubcategory(null);
                  setSubcategoryFormData({ slug: '', nameRu: '', nameEn: '', description: '' });
                }}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Slug *
                </label>
                <input
                  type="text"
                  required
                  value={subcategoryFormData.slug}
                  onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, slug: e.target.value })}
                  disabled={!!editingSubcategory}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 dark:disabled:bg-gray-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Название (RU) *
                </label>
                <input
                  type="text"
                  required
                  value={subcategoryFormData.nameRu}
                  onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, nameRu: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Название (EN) *
                </label>
                <input
                  type="text"
                  required
                  value={subcategoryFormData.nameEn}
                  onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, nameEn: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Описание
                </label>
                <textarea
                  value={subcategoryFormData.description}
                  onChange={(e) => setSubcategoryFormData({ ...subcategoryFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingSubcategory(null);
                    setSubcategoryFormData({ slug: '', nameRu: '', nameEn: '', description: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={editingSubcategory ? handleUpdateSubcategory : handleCreateSubcategory}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  {editingSubcategory ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
